import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { isAppShell } from '../../app-shell/is-app-shell.utility';
import { WorkloadQueryParameter } from '../constants/workload-query-parameter.enum';
import { FileItem, FileType } from '../models/file-item.model';
import { ConfigParserService } from '../services/config-parser.service';
import { DeviceDetector, PlatformType } from '../services/device-detector.service';
import { FeatureManagerService } from '../services/feature-manager.service';
import { WebPwaService } from '../services/web-pwa.service';
import { WindowService } from '../services/window.service';

export interface CapabilitiesConfig {
    accountSwitchingEnabled: boolean;
    activityBasedTimeoutEnabled: boolean;
    addToDoEnabled: boolean;
    appBarEnabled: boolean;
    appBarPinningEnabled: boolean;
    appGalleryReactEnabled: boolean;
    appManifestEnabled: boolean;
    appsInHero: boolean;
    altaBrandingChangesEnabled: boolean;
    contextMenu20Enabled: boolean;
    convertToPdfEnabled: boolean;
    batchGraphEnabled: boolean;
    bottomTrayEnabled: boolean;
    calendarEnabled: boolean;
    capPopupEnabled: boolean;
    capturingIntentEnabled: boolean;
    clientTokenFetchEnabled: boolean;
    contentActionsEnabled: boolean;
    contentActionsOHomeEnabled: boolean;
    contentLauncherEnabled: boolean;
    downloadActionEnabled: boolean;
    spacesEnabled: boolean;
    consumerThemesCalloutEnabled: boolean;
    createBackgroundEnabled: boolean;
    createButtonWithAnimationEnabled: boolean;
    createCenterEnabled: boolean;
    createCenterReactEnabled: boolean;
    createDocAspxExcelEnabled: boolean;
    createDocAspxPowerPointEnabled: boolean;
    createDocAspxVisioEnabled: boolean;
    createDocAspxWordEnabled: boolean;
    createDocumentSupported: boolean;
    createOneDriveDocApiEnabled: boolean;
    createOneNotePageEnabled: boolean;
    createOnHomeEnabled: boolean;
    createPaneSvgsEnabled: boolean;
    createSharingLinkApiEnabled: boolean;
    createStreamPlaylistEnabled: boolean;
    createStreamRecorderEnabled: boolean;
    createVisioEnabled: boolean;
    clientPwasEnabled: boolean;
    darkModeEnabled: boolean;
    darkModeOverride: boolean;
    delayedServiceWorkerInit: boolean;
    detailedConsumerHeadersEnabled: boolean;
    disableAnimation: boolean;
    edgeworthEnabled: boolean;
    edgeworthReactEnabled: boolean;
    eduPageTourFreEnabled: boolean;
    embedAppCachingEnabled: boolean;
    embedHost: string;
    embedModeEnabled: boolean;
    embedThemingEnabled: boolean;
    errorDialogEnabled: boolean;
    favoriteTemplatesEnabled: boolean;
    featureFlagsEnabled: boolean;
    filterPillsEnabled: boolean;
    fluentHeader: boolean;
    fluentRefresh: boolean;
    fluentIconFontEnabled: boolean;
    fluidAudience: string;
    fluidCapPopupEnabled: boolean;
    fluidCreateAbpEnabled: boolean;
    fluidCreateNewEnabled: boolean;
    fluidDevBuildEnabled: boolean;
    fluidFirst: boolean;
    fluidMipEnabled: boolean;
    fluidMockMipEnabled: boolean;
    fluidSourcelessIframeEnabled: boolean;
    fluidSupportEnabled: boolean;
    fluidUseServiceDeliveredDriver: boolean;
    fluidVersionHistoryEnabled: boolean;
    freNewCalloutsEnabled: boolean;
    freV2UiEnabled: boolean;
    freV2DimeEnabled: boolean;
    hasCalendarWorkload: boolean;
    hasExchangeLicense: boolean;
    highResRecThumbnailsEnabled: boolean;
    hoverPreloadTelemetryEnabled: boolean;
    heroBackgroundOptimizationEnabled: boolean;
    heroGradient: boolean;
    incrementalCalloutEnabled: boolean;
    isConsumerUser: boolean;
    isEmbedMode: boolean;
    isPwaInUwp: boolean;
    isPwaInWebView2: boolean;
    leftNavCalloutEnabled: boolean;
    leftNavReact: boolean;
    leftNavEmptyImage: string;
    limitNetworkRequests: boolean;
    listsCreateEnabled: boolean;
    maxInitialPlaces: number;
    maxTotalPlaces: number;
    memoryProfilingEnabled: boolean;
    metaExtensionsFetchEnabled: boolean;
    mobileContextMenusEnabled: boolean;
    modalFreCalloutEnabled: boolean;
    msaClientTokensEnabled: boolean;
    m365AppsAboutEnabled: boolean;
    m365AppsEnabled: boolean;
    m365TestAppEnabled: boolean;
    modernStartPageEnabled: boolean;
    modernStartPageRootPath: string;
    myContentEnabled: boolean;
    myContentGridViewEnabled: boolean;
    m365AppsPageRootPath: string;
    m365AppsSecondTab: boolean;
    nativeSearchInHeaderEnabled: boolean;
    newConsumerExperienceEnabled: boolean;
    newConsumerOverride: boolean;
    newUserShelfEnabled: boolean;
    newUserShelfOverride: boolean;
    northStarEnabled: boolean;
    officeHomeFloodgate: boolean;
    oneDriveApisEnabled: boolean;
    oneDriveUploadEnabled: boolean;
    otherFlights: string;
    outgoingLoginHintEnabled: boolean;
    parkEnabled: boolean;
    paidConsumerFreFloodgateEnabled: boolean;
    peopleCardsEnabled: boolean;
    pinningPeopleEnabled: boolean;
    powerBiCreateEnabled: boolean;
    prefetchDataEnabled: boolean;
    pwaCommercialNonEduFreEnabled: boolean;
    pwaEnabled: boolean;
    pwaFreeConsumerFreEnabled: boolean;
    pullToRefreshEnabled: boolean;
    quickAccessV1Enabled: boolean;
    quickAccessV1GridViewEnabled: boolean;
    quickAccessV1CustomPivotEnabled: boolean;
    recommendedDeepLinkEnabled: boolean;
    recommendedFloodgate: boolean;
    recommendedReact: boolean;
    recommendedTeamsLinkEnabled: boolean;
    redirectWhenSignedOutFromWorkloads: boolean;
    reloadWhenAuthExpired: boolean;
    reloadWhenPolicyCheckFailed: boolean;
    reloadWhenReauthRequired: boolean;
    renderSpaceAsFluid: boolean;
    replacePinActionEnabled: boolean;
    scheduleEnabled: boolean;
    secondaryTokensEnabled: boolean;
    sharePointEnabled: boolean;
    shareToTeamsEnabled: boolean;
    shellDocumentTitleEnabledInFluid: boolean;
    shellFeedbackEnabled: boolean;
    shellPinEnabled: boolean;
    showAppLauncherRedesign: boolean;
    showA2HSPrompt: boolean;
    showBusinessApps: boolean;
    showFeedback: boolean;
    showFloodgate: boolean;
    showFolders: boolean;
    showPwaFeelingsSurvey: boolean;
    showSearch: boolean;
    showThemingCallout: boolean;
    sideloadAppButtonEnabled: boolean;
    storeAppEnabled: boolean;
    startPageAppsArray: Array<string>;
    startPageApps?: Set<FileType>;
    startPageRootPath: string;
    stayingAwareEnabled: boolean;
    stayingAwareRQEnabled: boolean;
    suggestedActionsEnabled: boolean;
    supportCentralEnabled: boolean;
    svgAppIconsEnabled: boolean;
    swayCreateNewEnabled: boolean;
    taskMeowEnabled: boolean;
    templatePageRootPath: string;
    templatesEnabled: boolean;
    themingCalloutEnabled: boolean;
    thirdPartyCreateEnabled: boolean;
    thumbnailEndpointEnabled: boolean;
    toDoM365AppEnabled: boolean;
    unifiedHostPath: string;
    universalMeControlPWAEnabled: boolean;
    useOdbUrlForVroom: boolean;
    userConsentCallEnabled: boolean;
    userPreferenceEnabled: boolean;
    useVroomForOneDriveApis: boolean;
    wacExcelJsApiV2Enabled: boolean;
    wacHostAppsArray: Array<string>;
    wacHostPrebootAppsArray: Array<string>;
    wacHostTimeoutTeardownEnabled: boolean;
    wacHostEnabled: boolean;
    wacHostControlFlight: boolean;
    wacHostHoverPreloadEnabled: boolean;
    wacHostApps?: Set<FileType>;
    wacHostPrebootApps?: Set<FileType>;
    wacHostPagePath: string;
    wacSharingLinkEnabled: boolean;
    wacStartPageEnabled: boolean;
    wacHostExternalLinkEnabled: boolean;
    wacHostOrgTemplatesEnabled: boolean;
    wacHostGetAuthTokenEnabled: boolean;
    wacHostGetCustomAuthConfigEnabled: boolean;
    wacWordJsApiV2Enabled: boolean;
    wacHostMoveToFolderEnabled: boolean;
    webVitalsEnabled: boolean;
    formsCalloutEnabled: boolean;
    scenarioId: string;
    wacHostHoverPrebootAppsArray: Array<string>;
    wacHostHoverPrebootApps?: Set<FileType>;
    wacHostHoverPrebootControlFlight: boolean;
    yammerCreateEnabled: boolean;
    yammerM365AppEnabled: boolean;
    whiteboardCreateEnabled: boolean;
    graphApiForV2ConsumerEnabled: boolean;
    adminPolicyEnabled: boolean;
    threeSRDLEnabled: boolean;
    threeSMRERDLEnabled: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class CapabilitiesService {
    public readonly openWithUIHost: Observable<boolean>;
    public readonly capabilities: CapabilitiesConfig;

    private shouldOpenWithUIHost = false;

    constructor(
        deviceDetector: DeviceDetector,
        configParserService: ConfigParserService,
        private readonly featureManagerService: FeatureManagerService,
        webPwaService: WebPwaService,
        windowService: WindowService
    ) {
        // Disable all capabilities if there was failure to read them from page response
        this.capabilities = configParserService.parse<CapabilitiesConfig>('capabilities') || {
            accountSwitchingEnabled: false,
            activityBasedTimeoutEnabled: false,
            addToDoEnabled: false,
            appBarEnabled: false,
            appBarPinningEnabled: false,
            appGalleryReactEnabled: false,
            appManifestEnabled: false,
            appsInHero: false,
            altaBrandingChangesEnabled: false,
            batchGraphEnabled: false,
            bottomTrayEnabled: false,
            calendarEnabled: false,
            capPopupEnabled: false,
            capturingIntentEnabled: false,
            clientTokenFetchEnabled: false,
            contentActionsEnabled: false,
            contentActionsOHomeEnabled: false,
            contentLauncherEnabled: false,
            spacesEnabled: false,
            consumerThemesCalloutEnabled: false,
            contextMenu20Enabled: false,
            convertToPdfEnabled: false,
            createBackgroundEnabled: false,
            createButtonWithAnimationEnabled: false,
            createCenterEnabled: false,
            createCenterReactEnabled: false,
            createDocAspxExcelEnabled: false,
            createDocAspxPowerPointEnabled: false,
            createDocAspxVisioEnabled: false,
            createDocAspxWordEnabled: false,
            createDocumentSupported: false,
            createOneDriveDocApiEnabled: false,
            createOneNotePageEnabled: false,
            createOnHomeEnabled: false,
            createPaneSvgsEnabled: false,
            createSharingLinkApiEnabled: false,
            createStreamPlaylistEnabled: false,
            createStreamRecorderEnabled: false,
            createVisioEnabled: false,
            clientPwasEnabled: false,
            darkModeEnabled: false,
            darkModeOverride: false,
            delayedServiceWorkerInit: false,
            detailedConsumerHeadersEnabled: false,
            downloadActionEnabled: false,
            disableAnimation: false,
            edgeworthEnabled: false,
            edgeworthReactEnabled: false,
            eduPageTourFreEnabled: false,
            embedAppCachingEnabled: false,
            embedHost: '',
            embedModeEnabled: false,
            embedThemingEnabled: false,
            errorDialogEnabled: false,
            favoriteTemplatesEnabled: false,
            featureFlagsEnabled: false,
            filterPillsEnabled: false,
            fluentHeader: false,
            fluentRefresh: false,
            fluentIconFontEnabled: false,
            fluidAudience: 'Unknown',
            fluidCapPopupEnabled: false,
            fluidCreateAbpEnabled: false,
            fluidCreateNewEnabled: false,
            fluidDevBuildEnabled: false,
            fluidFirst: false,
            fluidMipEnabled: false,
            fluidMockMipEnabled: false,
            fluidSourcelessIframeEnabled: false,
            fluidSupportEnabled: false,
            fluidUseServiceDeliveredDriver: false,
            fluidVersionHistoryEnabled: false,
            freNewCalloutsEnabled: false,
            freV2UiEnabled: false,
            freV2DimeEnabled: false,
            hasCalendarWorkload: false,
            hasExchangeLicense: false,
            highResRecThumbnailsEnabled: false,
            hoverPreloadTelemetryEnabled: false,
            heroBackgroundOptimizationEnabled: true,
            heroGradient: true,
            incrementalCalloutEnabled: false,
            isConsumerUser: false,
            isEmbedMode: false,
            isPwaInUwp: false,
            isPwaInWebView2: false,
            leftNavCalloutEnabled: false,
            leftNavReact: false,
            leftNavEmptyImage: '',
            limitNetworkRequests: false,
            listsCreateEnabled: false,
            maxInitialPlaces: 0,
            maxTotalPlaces: 0,
            memoryProfilingEnabled: false,
            metaExtensionsFetchEnabled: false,
            mobileContextMenusEnabled: false,
            modalFreCalloutEnabled: false,
            msaClientTokensEnabled: false,
            m365AppsAboutEnabled: false,
            m365AppsEnabled: false,
            m365TestAppEnabled: false,
            modernStartPageEnabled: false,
            modernStartPageRootPath: null,
            myContentEnabled: false,
            myContentGridViewEnabled: false,
            m365AppsPageRootPath: null,
            m365AppsSecondTab: false,
            nativeSearchInHeaderEnabled: false,
            newConsumerExperienceEnabled: false,
            newConsumerOverride: false,
            newUserShelfEnabled: false,
            newUserShelfOverride: false,
            northStarEnabled: false,
            officeHomeFloodgate: false,
            oneDriveApisEnabled: false,
            oneDriveUploadEnabled: false,
            otherFlights: '',
            outgoingLoginHintEnabled: false,
            paidConsumerFreFloodgateEnabled: false,
            parkEnabled: false,
            peopleCardsEnabled: false,
            pinningPeopleEnabled: false,
            powerBiCreateEnabled: false,
            prefetchDataEnabled: false,
            pwaCommercialNonEduFreEnabled: false,
            pwaEnabled: false,
            pwaFreeConsumerFreEnabled: false,
            pullToRefreshEnabled: false,
            quickAccessV1Enabled: false,
            quickAccessV1GridViewEnabled: false,
            quickAccessV1CustomPivotEnabled: false,
            recommendedDeepLinkEnabled: false,
            recommendedFloodgate: false,
            recommendedReact: false,
            recommendedTeamsLinkEnabled: false,
            redirectWhenSignedOutFromWorkloads: true,
            reloadWhenAuthExpired: true,
            reloadWhenPolicyCheckFailed: true,
            reloadWhenReauthRequired: true,
            renderSpaceAsFluid: true,
            replacePinActionEnabled: false,
            scheduleEnabled: false,
            secondaryTokensEnabled: false,
            sharePointEnabled: false,
            shareToTeamsEnabled: false,
            shellDocumentTitleEnabledInFluid: false,
            shellFeedbackEnabled: false,
            shellPinEnabled: false,
            showAppLauncherRedesign: false,
            showA2HSPrompt: false,
            showBusinessApps: false,
            showFeedback: false,
            showFloodgate: false,
            showFolders: false,
            showPwaFeelingsSurvey: false,
            showSearch: false,
            showThemingCallout: false,
            sideloadAppButtonEnabled: false,
            storeAppEnabled: false,
            startPageAppsArray: new Array<string>(),
            startPageRootPath: null,
            stayingAwareEnabled: false,
            stayingAwareRQEnabled: false,
            suggestedActionsEnabled: false,
            supportCentralEnabled: false,
            svgAppIconsEnabled: false,
            swayCreateNewEnabled: false,
            taskMeowEnabled: false,
            templatePageRootPath: null,
            templatesEnabled: false,
            themingCalloutEnabled: false,
            thirdPartyCreateEnabled: false,
            thumbnailEndpointEnabled: false,
            toDoM365AppEnabled: false,
            unifiedHostPath: '',
            universalMeControlPWAEnabled: false,
            useOdbUrlForVroom: false,
            userConsentCallEnabled: false,
            userPreferenceEnabled: false,
            useVroomForOneDriveApis: false,
            wacExcelJsApiV2Enabled: false,
            wacHostAppsArray: new Array<string>(),
            wacHostPrebootAppsArray: new Array<string>(),
            wacHostTimeoutTeardownEnabled: false,
            wacHostEnabled: false,
            wacHostControlFlight: false,
            wacHostHoverPreloadEnabled: false,
            wacHostPagePath: '',
            wacSharingLinkEnabled: false,
            wacStartPageEnabled: false,
            wacHostExternalLinkEnabled: false,
            wacHostOrgTemplatesEnabled: false,
            wacHostGetAuthTokenEnabled: false,
            wacHostGetCustomAuthConfigEnabled: false,
            wacWordJsApiV2Enabled: false,
            wacHostMoveToFolderEnabled: false,
            webVitalsEnabled: false,
            formsCalloutEnabled: false,
            scenarioId: null,
            wacHostHoverPrebootAppsArray: new Array<string>(),
            wacHostHoverPrebootControlFlight: false,
            yammerCreateEnabled: false,
            yammerM365AppEnabled: false,
            whiteboardCreateEnabled: false,
            graphApiForV2ConsumerEnabled: false,
            adminPolicyEnabled: false,
            threeSRDLEnabled: false,
            threeSMRERDLEnabled: false
        };

        if (this.capabilities.featureFlagsEnabled) {
            this.updateWithOverrides();
        }

        this.capabilities.oneDriveUploadEnabled = this.capabilities.oneDriveUploadEnabled && deviceDetector.isDesktopDevice();
        this.capabilities.createDocumentSupported = !deviceDetector.isMobileDevice() && !deviceDetector.isTabletDevice(PlatformType.AndroidTablet);
        this.capabilities.isPwaInUwp = this.capabilities.pwaEnabled && deviceDetector.isUwp();
        this.capabilities.isPwaInWebView2 = this.capabilities.pwaEnabled && deviceDetector.isWebView2();
        this.capabilities.disableAnimation = this.capabilities.pwaEnabled;
        this.capabilities.mobileContextMenusEnabled = deviceDetector.isMobileDevice();
        this.capabilities.leftNavReact = !deviceDetector.isMobileDevice();
        this.capabilities.appBarEnabled = this.capabilities.appBarEnabled && !deviceDetector.isMobileDevice();
        this.capabilities.suggestedActionsEnabled = this.capabilities.suggestedActionsEnabled && !deviceDetector.isMobileDevice();

        // Content actions
        if (!this.capabilities.contentActionsEnabled) {
            this.capabilities.addToDoEnabled = false;
            this.capabilities.contextMenu20Enabled = false;
            this.capabilities.convertToPdfEnabled = false;
            this.capabilities.scheduleEnabled = false;
            this.capabilities.shareToTeamsEnabled = false;
            this.capabilities.createSharingLinkApiEnabled = false;
            this.capabilities.contentActionsOHomeEnabled = false;
        }

        // Preinstalled MetaOs Apps
        if (!this.capabilities.m365AppsEnabled) {
            this.capabilities.m365TestAppEnabled = false;
            this.capabilities.parkEnabled = false;
            this.capabilities.storeAppEnabled = false;
            this.capabilities.taskMeowEnabled = false;
            this.capabilities.toDoM365AppEnabled = false;
            this.capabilities.yammerM365AppEnabled = false;
        }

        if (isAppShell()) {
            this.capabilities.clientTokenFetchEnabled = true;
        }

        if (this.capabilities.wacHostAppsArray) {
            this.capabilities.wacHostApps = new Set<FileType>(this.capabilities.wacHostAppsArray.map(app =>
                FileItem.fileTypeNameToFileType(app)));
        }

        if (this.capabilities.startPageAppsArray) {
            this.capabilities.startPageApps = new Set<FileType>(this.capabilities.startPageAppsArray.map(app =>
                FileItem.fileTypeNameToFileType(app)));
        }

        if (this.capabilities.wacHostPrebootAppsArray) {
            this.capabilities.wacHostPrebootApps = new Set<FileType>(this.capabilities.wacHostPrebootAppsArray.map(app =>
                FileItem.fileTypeNameToFileType(app)));
        }

        if (this.capabilities.wacHostHoverPrebootAppsArray) {
            this.capabilities.wacHostHoverPrebootApps = new Set<FileType>(this.capabilities.wacHostHoverPrebootAppsArray.map(app =>
                FileItem.fileTypeNameToFileType(app)));
        }

        this.openWithUIHost = webPwaService.isStandalone.pipe(
            map(isWebPwa => this.capabilities.wacHostEnabled || (this.capabilities.clientPwasEnabled && isWebPwa))
        );

        this.openWithUIHost.subscribe(enabled => this.shouldOpenWithUIHost = enabled);

        if (typeof this.capabilities.templatesEnabled === 'undefined') {
            // In the PWA, it's possible the templatesEnabled setting is not in a previously cached config API response.
            // To make sure we don't disable templates in that case, check that the page is on office.com WW. Templates are
            // only disabled on sovereign environments (ending in .us, .mil, .de).
            // TODO: remove this if block once this change is stable in prod.
            const hostname = windowService.nativeWindow.location.hostname;
            this.capabilities.templatesEnabled = hostname && hostname.lastIndexOf('.com') === hostname.length - 4;
        }

        // Enable EdgeWorth on WebView2 version of HWA, but disable on Edge Spartan version of HWA
        this.capabilities.edgeworthEnabled = this.capabilities.edgeworthEnabled && 'Proxy' in windowService.nativeWindow && (!this.capabilities.isPwaInUwp || this.capabilities.isPwaInWebView2);
    }

    public wacHostHoverPrebootEnabledForFileType(fileType: FileType): boolean {
        const wacHostHoverPrebootApps = this.capabilities.wacHostHoverPrebootApps;
        return !!wacHostHoverPrebootApps && wacHostHoverPrebootApps.has(fileType);
    }

    // TODO: Move to wac content service once DI issue is resolved
    public wacHostEnabledForFileType(fileType: FileType): boolean {
        const wacHostApps = this.capabilities.wacHostApps;
        return this.wacHostEnabled() && (!!wacHostApps && wacHostApps.has(fileType));
    }

    public wacHostEnabled(): boolean {
        return this.shouldOpenWithUIHost;
    }

    public getWorkload(): WorkloadQueryParameter {
        if (this.capabilities.pwaEnabled) {
            return WorkloadQueryParameter.Pwa;
        }
        if (this.capabilities.isEmbedMode) {
            return WorkloadQueryParameter.Embed;
        }
        return WorkloadQueryParameter.OfficeHome;
    }

    private updateWithOverrides() {
        const flagsToIgnore = new Set([
            'featureFlagsEnabled',
            'disableAnimation',
            'createDocumentSupported'
        ]);

        const newConfig = this.featureManagerService.registerFlagsAndGetConfig(
            FeatureManagerService.parseBooleanFlagsAndFilter(this.capabilities, flagsToIgnore)
        );

        const keys = Object.keys(newConfig);
        for (const key of keys) {
            this.capabilities[key] = newConfig[key];
        }
    }
}
